import { ensureNoUndefinedProperties } from '@/Shared/Helper/EnsureNoUndefinedProperties'
import MeetingID from '@/App/Booking/Meeting/Entity/MeetingID'
import {
    MeetingStateEnum,
    MeetingStateEnumData,
    SchedulingStrategyEnum,
    type MeetingData,
    type MeetingExternalEventSyncStateEnum,
    type MeetingLocationTypeEnum,
    type MeetingStateEnumDataEntry,
    type MeetingSearchFiltersType,
} from '@/Shared/AutoGenerated/Main'
import type HasPaymentStateInterface from '@/App/Booking/Shared/Entity/HasPaymentStateInterface'
import type HasSchedulingStateInterface from '@/App/Booking/Shared/Entity/HasSchedulingStateInterface'
import type {
    SchedulingStateBadgeColor,
    SchedulingStateBadgeLabel,
} from '@/App/Booking/Shared/Entity/HasSchedulingStateInterface'
import CompanyCustomerID from '@/App/Company/Entity/CompanyCustomerID'
import BookingID from '@/App/Booking/Booking/Entity/BookingID'
import type { MeetingsLocationDataInterface } from '@/Shared/AutoGenerated/Types'

export default class Meeting
    implements HasSchedulingStateInterface, HasPaymentStateInterface
{
    constructor(
        public readonly id: MeetingID,
        public readonly bookingId: BookingID,
        public readonly state: MeetingStateEnum,
        public readonly uniqueCode: string,
        public readonly duration: number,
        public readonly locationType: MeetingLocationTypeEnum | null,
        public readonly locationData: object | null,
        public readonly meetingLocationData: MeetingsLocationDataInterface | null,
        public readonly externalEventSyncState: MeetingExternalEventSyncStateEnum | null,
        public readonly snapshotIsPaymentRequired: boolean,
        public readonly snapshotSchedulingStrategy: SchedulingStrategyEnum,
        public readonly recurringMeetingOrder: number,
        // Computed
        public readonly isRecurring: boolean,
        public readonly isRefunded: boolean,
        public readonly dateTimeFormatted: string | null,
        public readonly dateFormatted: string | null,
        public readonly startAtFormatted: string | null,
        public readonly endAtFormatted: string | null,
        public readonly dayOfWeekFormatted: string | null,
    ) {
        ensureNoUndefinedProperties(this)
    }

    getLocationData(): MeetingsLocationDataInterface {
        if (!this.meetingLocationData) {
            throw new Error('Location data is not available')
        }

        return this.meetingLocationData
    }

    // ------------------------
    // HasSchedulingStateInterface implementation
    // ------------------------

    getSchedulingStateBadgeLabel(): SchedulingStateBadgeLabel {
        return this.getSchedulingStateDataEntry().label
    }

    getSchedulingStateDataEntry(): MeetingStateEnumDataEntry {
        return MeetingStateEnumData[this.state]
    }

    getSchedulingStateBadgeColor(): SchedulingStateBadgeColor {
        if (this.state === MeetingStateEnum.Scheduled) {
            return 'success'
        }

        if (this.state === MeetingStateEnum.Canceled) {
            return 'secondary'
        }

        if (this.state === MeetingStateEnum.Completed) {
            return 'info'
        }

        if (this.state === MeetingStateEnum.NoShow) {
            return 'warning'
        }

        throw new Error(`Unknown meeting state ${this.state}`)
    }

    // ------------------------
    // HasPaymentStateInterface implementation
    // ------------------------

    getPaymentStateBadgeColor(): 'info' | 'secondary' {
        if (!this.snapshotIsPaymentRequired) {
            throw new Error('Meeting is not paid')
        }

        if (this.isRefunded) {
            return 'secondary'
        }

        if (!this.isRefunded) {
            return 'info'
        }

        throw new Error(`Invalid meeting payment state ${this.id}`)
    }

    getPaymentStateBadgeLabel(): 'Paid' | 'Refunded' {
        if (!this.snapshotIsPaymentRequired) {
            throw new Error('Meeting is not paid')
        }

        if (this.isRefunded) {
            return 'Refunded'
        }

        if (!this.isRefunded) {
            return 'Paid'
        }

        throw new Error(`Invalid meeting payment state ${this.id}`)
    }

    // ------------------------
    // Hydration
    // ------------------------

    static hydrateSingle(entityData: MeetingData): Meeting {
        return new Meeting(
            new MeetingID(entityData.id),
            new BookingID(entityData.bookingId),
            entityData.state,
            entityData.uniqueCode,
            entityData.duration,
            entityData.locationType,
            entityData.locationData,
            entityData.meetingLocationData,
            entityData.externalEventSyncState,
            entityData.snapshotIsPaymentRequired,
            entityData.snapshotSchedulingStrategy,
            entityData.recurringMeetingOrder,
            // Computed
            entityData.isRecurring,
            entityData.isRefunded,
            entityData.dateTimeFormatted,
            entityData.dateFormatted,
            entityData.startAtFormatted,
            entityData.endAtFormatted,
            entityData.dayOfWeekFormatted,
        )
    }

    static hydrateMultiple(entitiesData: MeetingData[]): Meeting[] {
        return entitiesData.map((entityData) => this.hydrateSingle(entityData))
    }
}
